import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Check, ChevronsUpDown, Home, Shield, Plus, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { useAuth } from '@/context/AuthContext';
import { useMyProducers, MyProducer } from '@/hooks/useMyProducers';

const LAST_PRODUCER_KEY = 'farmly:lastProducerId';

type PortalMode = 'admin' | 'producer' | 'customer' | 'editor';

interface TopbarContextSwitcherProps {
  mode?: PortalMode;
  currentProducerId?: string;
  currentProducerName?: string;
}

export function TopbarContextSwitcher({
  mode: modeProp,
  currentProducerId,
  currentProducerName,
}: TopbarContextSwitcherProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin } = useAuth();
  const [open, setOpen] = useState(false);

  // Infer mode from route if not provided
  const mode: PortalMode =
    modeProp ??
    (location.pathname.startsWith('/admin') || location.pathname.startsWith('/app/admin')
      ? 'admin'
      : location.pathname.startsWith('/app/editor')
        ? 'editor'
        : location.pathname.startsWith('/account') || location.pathname.startsWith('/app/customer')
          ? 'customer'
          : 'producer');

  // Lazy-load producers only when popover is open; skip entirely in admin mode
  const { producers, isLoading: producersLoading } = useMyProducers({
    enabled: open && mode !== 'admin',
  });

  const getLastProducerId = (): string | null => {
    try {
      return localStorage.getItem(LAST_PRODUCER_KEY);
    } catch {
      return null;
    }
  };

  const setLastProducerId = (id: string) => {
    try {
      localStorage.setItem(LAST_PRODUCER_KEY, id);
    } catch {
      // Ignore localStorage errors
    }
  };

  useEffect(() => {
    if (currentProducerId && currentProducerId !== 'demo') {
      setLastProducerId(currentProducerId);
    }
  }, [currentProducerId]);

  const handleSelectAdmin = () => {
    setOpen(false);
    navigate('/admin/producers');
  };

  const handleSelectProducerPortal = () => {
    setOpen(false);
    const lastId = getLastProducerId();
    const targetId = lastId || producers[0]?.id;

    if (targetId) {
      navigate(`/producer/farm/${targetId}`);
    } else {
      navigate('/producer');
    }
  };

  const handleSelectProducer = (producer: MyProducer) => {
    setOpen(false);
    setLastProducerId(producer.id);

    if (mode === 'producer' && currentProducerId) {
      const currentPath = location.pathname;
      const basePath = `/producer/farm/${currentProducerId}`;
      const subPath = currentPath.startsWith(basePath)
        ? currentPath.slice(basePath.length)
        : '';
      navigate(`/producer/farm/${producer.id}${subPath}`);
    } else {
      navigate(`/producer/farm/${producer.id}`);
    }
  };

  const handleCreateFarm = () => {
    setOpen(false);
    navigate('/for-producers/qualify');
  };

  const displayIcon =
    mode === 'admin' ? '🛡️' : mode === 'editor' ? '✏️' : mode === 'customer' ? '👤' : '🏡';

  const displayText =
    mode === 'admin'
      ? 'Admin'
      : mode === 'editor'
        ? 'Redaktion'
        : mode === 'customer'
          ? 'Mitt konto'
          : currentProducerName || 'Välj gård';

  const roleBadgeVariant =
    mode === 'admin'
      ? 'destructive'
      : mode === 'editor'
        ? 'outline'
        : mode === 'customer'
          ? 'secondary'
          : 'default';

  const roleLabel =
    mode === 'admin'
      ? 'ADMIN'
      : mode === 'editor'
        ? 'PLATSREDAKTÖR'
        : mode === 'customer'
          ? 'BESÖKARE'
          : 'VÄRD';

  return (
    <div className="flex items-center gap-2">
      {roleLabel !== 'BESÖKARE' && (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              role="combobox"
              aria-expanded={open}
              className="h-9 px-3 justify-start gap-2 font-medium"
            >
              <span className="text-base">{displayIcon}</span>
              <span className="truncate max-w-[160px] sm:max-w-[200px]">
                {displayText}
              </span>
              <ChevronsUpDown className="ml-1 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>

          <PopoverContent className="w-[280px] p-0" align="start">
            <Command>
              <CommandInput placeholder="Sök..." />
              <CommandList>
                <CommandEmpty>Inga resultat hittades.</CommandEmpty>

                <CommandGroup heading="Vy">
                  <CommandItem
                    onSelect={handleSelectProducerPortal}
                    className="flex items-center gap-2"
                  >
                    <Home className="h-4 w-4" />
                    <span>Värdportal</span>
                    {mode === 'producer' && <Check className="ml-auto h-4 w-4" />}
                  </CommandItem>

                  {isAdmin && (
                    <CommandItem
                      onSelect={handleSelectAdmin}
                      className="flex items-center gap-2"
                    >
                      <Shield className="h-4 w-4" />
                      <span>Adminportal</span>
                      {mode === 'admin' && <Check className="ml-auto h-4 w-4" />}
                    </CommandItem>
                  )}
                </CommandGroup>

                {producers.length > 0 && (
                  <>
                    <CommandSeparator />
                    <CommandGroup heading="Mina gårdar">
                      {producersLoading ? (
                        <div className="flex items-center justify-center py-4">
                          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                        </div>
                      ) : (
                        producers.map((producer) => (
                          <CommandItem
                            key={producer.id}
                            onSelect={() => handleSelectProducer(producer)}
                            className="flex items-center gap-2"
                          >
                            <span className="text-base">🏡</span>
                            <div className="flex flex-col flex-1 min-w-0">
                              <span className="truncate">{producer.name}</span>
                              {producer.city && (
                                <span className="text-xs text-muted-foreground truncate">
                                  {producer.city}
                                </span>
                              )}
                            </div>
                            {currentProducerId === producer.id && mode === 'producer' && (
                              <Check className="ml-auto h-4 w-4 shrink-0" />
                            )}
                          </CommandItem>
                        ))
                      )}
                    </CommandGroup>
                  </>
                )}

                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    onSelect={handleCreateFarm}
                    className="flex items-center gap-2 text-muted-foreground"
                  >
                    <Plus className="h-4 w-4" />
                    <span>Skapa ny gård</span>
                  </CommandItem>
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      )}

      <Badge
        variant={roleBadgeVariant}
        className="text-[10px] uppercase tracking-wide hidden sm:inline-flex"
      >
        {roleLabel}
      </Badge>
    </div>
  );
}